/* global Office, window */

// Import `readApiError` from the leaf `utils/auth` module rather than
// the `utils` barrel so auth wiring does not depend on unrelated utility
// exports.
import { readApiError } from "../utils/apiError";
import { apiFetch } from "../apiClient";
import type { DataSourceFeatureFlags } from "../api/finster/constants";
import { shouldAttemptFactSetAutoLogin } from "./factsetAutoLogin";
import { resetSkillsCatalogStore } from "../skills/catalogStore";
import { resetUserFilesStore } from "../api/finster/userFilesStore";

export { shouldAttemptFactSetAutoLogin } from "./factsetAutoLogin";

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  companyId?: string;
}

export interface PublicFeatures {
  auth?: {
    mode?: string;
    appEnv?: string;
  };
  /** Public Finster web-app base URL used for outbound management links. */
  finsterWebUrl?: string;
  featureFlags?: DataSourceFeatureFlags;
  /** ConfigCat client SDK key (public); used when the build-time key is absent. */
  configcatSdkKey?: string;
  /**
   * Server-gated Excel tabular-query transport and run-scoped throughput limits.
   * The limits are sent only while the gate is open.
   */
  excelTabularQuery?: {
    enabled: boolean;
    columnMaxInFlightBatches?: number;
    cellMaxInFlightSingles?: number;
  };
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  features?: PublicFeatures;
}

export interface SsoLoginOptions {
  method?: "factset" | "sso";
  organizationId?: string;
  connectionId?: string;
  loginHint?: string;
}

export type LoginStrategy =
  | { type: "password" }
  | {
      type: "sso";
      organizationId?: string;
      connectionId?: string;
      loginHint?: string;
    };

const EMPTY_AUTH_STATE: AuthState = {
  isAuthenticated: false,
  user: null,
};
const WORKOS_STATE_STORAGE_KEY = "workos_state";

function hasOfficeDialog(): boolean {
  try {
    return typeof Office !== "undefined" && !!Office.context?.ui?.displayDialogAsync;
  } catch {
    return false;
  }
}

/**
 * Return the default unauthenticated state. Real session state
 * is loaded from the backend so tokens never live in browser storage.
 */
export function getAuthState(): AuthState {
  return EMPTY_AUTH_STATE;
}

/**
 * Load the active server session using the HttpOnly session cookie.
 */
export async function fetchSession(): Promise<AuthState> {
  const response = await apiFetch("/api/auth/session", {
    method: "GET",
  });

  if (response.status === 401) {
    return EMPTY_AUTH_STATE;
  }

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }

  const data = await response.json();
  return {
    isAuthenticated: true,
    user: {
      id: data.user.id,
      email: data.user.email,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      profilePictureUrl: data.user.profilePictureUrl,
      companyId: data.user.companyId,
    },
    features: data.features,
  };
}

async function getAuthorizationUrl(options: SsoLoginOptions = {}): Promise<string> {
  const params = new URLSearchParams();
  if (options.method) params.set("method", options.method);
  if (options.organizationId) params.set("organizationId", options.organizationId);
  if (options.connectionId) params.set("connectionId", options.connectionId);
  if (options.loginHint) params.set("loginHint", options.loginHint);
  const queryString = params.toString();
  const response = await apiFetch(`/api/auth/authorize-url${queryString ? `?${queryString}` : ""}`, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }

  const data = await response.json();
  return data.authorizationUrl;
}

export async function identifyLogin(email: string): Promise<LoginStrategy> {
  const response = await apiFetch("/api/auth/login/identify", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }

  return response.json();
}

export async function loginWithPassword(email: string, password: string): Promise<AuthState> {
  const response = await apiFetch("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }

  return fetchSession();
}

export async function requestPasswordReset(email: string): Promise<string> {
  const response = await apiFetch("/api/auth/password-reset", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }

  const data = await response.json();
  return data.message || "If an account exists for that email, we've sent password reset instructions.";
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  const response = await apiFetch("/api/auth/password-reset/confirm", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
}

interface DialogAuthResult {
  code?: string;
  state?: string;
  error?: string;
}

function rememberExpectedState(authUrl: string): void {
  try {
    const state = new URL(authUrl).searchParams.get("state");
    if (state) {
      sessionStorage.setItem(WORKOS_STATE_STORAGE_KEY, state);
    }
  } catch {
    // Ignore malformed URLs and continue; server validates state.
  }
}

function consumeExpectedState(): string | null {
  try {
    const expected = sessionStorage.getItem(WORKOS_STATE_STORAGE_KEY);
    sessionStorage.removeItem(WORKOS_STATE_STORAGE_KEY);
    return expected;
  } catch {
    return null;
  }
}

function validateReturnedState(returnedState?: string): void {
  const expectedState = consumeExpectedState();
  if (expectedState && expectedState !== returnedState) {
    throw new Error("Invalid authentication state.");
  }
}

async function exchangeCodeForSession(code: string, state?: string): Promise<void> {
  const response = await apiFetch("/api/auth/exchange", {
    method: "POST",
    body: JSON.stringify({ code, state }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
}

/**
 * Open WorkOS login using the Office dialog API.
 */
function openLoginDialogOffice(options: SsoLoginOptions = {}): Promise<DialogAuthResult> {
  return new Promise((resolve, reject) => {
    getAuthorizationUrl(options)
      .then((authUrl) => {
        rememberExpectedState(authUrl);
        Office.context.ui.displayDialogAsync(
          authUrl,
          { height: 60, width: 30, promptBeforeOpen: false },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(result.error.message));
              return;
            }

            const dialog = result.value;
            let settled = false;

            const settleResolve = (message: DialogAuthResult) => {
              if (settled) return;
              settled = true;
              try {
                dialog.close();
              } catch {
                // Ignore close errors
              }
              resolve(message);
            };

            const settleReject = (error: Error) => {
              if (settled) return;
              settled = true;
              try {
                dialog.close();
              } catch {
                // Ignore close errors
              }
              reject(error);
            };

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
              try {
                const message = JSON.parse(
                  (arg as { message: string }).message
                ) as DialogAuthResult;
                if (message.error) {
                  settleReject(new Error(message.error));
                } else {
                  settleResolve(message);
                }
              } catch {
                settleReject(new Error("Failed to parse auth response"));
              }
            });

            dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
              const eventArg = arg as { error: number };
              if (eventArg.error === 12006) {
                settleReject(new Error("Login cancelled"));
              } else {
                settleReject(new Error(`Login dialog error (${eventArg.error})`));
              }
            });
          }
        );
      })
      .catch(reject);
  });
}

/**
 * Fallback: open WorkOS login in a browser popup when Office JS is unavailable.
 * The callback page posts the auth result back via `window.postMessage`.
 */
function openLoginDialogPopup(options: SsoLoginOptions = {}): Promise<DialogAuthResult> {
  return new Promise((resolve, reject) => {
    getAuthorizationUrl(options)
      .then((authUrl) => {
        rememberExpectedState(authUrl);

        const width = 500;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
          authUrl,
          "finster_auth",
          `width=${width},height=${height},left=${left},top=${top},popup=yes`
        );

        if (!popup) {
          reject(new Error("Popup blocked — please allow popups for this site."));
          return;
        }

        const cleanup = () => {
          window.removeEventListener("message", onMessage);
          clearInterval(pollTimer);
        };

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          let data: DialogAuthResult;
          try {
            data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          } catch {
            return;
          }

          if (!data || (!data.code && !data.error)) return;

          cleanup();
          try {
            popup.close();
          } catch {
            /* already closed */
          }

          if (data.error) {
            reject(new Error(data.error));
          } else {
            resolve(data);
          }
        };

        window.addEventListener("message", onMessage);

        const pollTimer = setInterval(() => {
          if (popup.closed) {
            cleanup();
            reject(new Error("Login cancelled"));
          }
        }, 500);
      })
      .catch(reject);
  });
}

function openLoginDialog(options: SsoLoginOptions = {}): Promise<DialogAuthResult> {
  if (hasOfficeDialog()) {
    return openLoginDialogOffice(options);
  }
  return openLoginDialogPopup(options);
}

/**
 * Full login flow
 */
export async function login(options: SsoLoginOptions = {}): Promise<AuthState> {
  try {
    const result = await openLoginDialog(options);

    if (!result.code) {
      consumeExpectedState();
      throw new Error("Invalid auth result");
    }

    validateReturnedState(result.state);
    await exchangeCodeForSession(result.code, result.state);
    return fetchSession();
  } catch (e) {
    console.error("Login failed:", e);
    throw e;
  }
}

/**
 * Logout by clearing the server session.
 * Chat history and other persisted artifacts are intentionally preserved
 * so they remain available when the user logs back in.
 */
export async function logout(): Promise<void> {
  try {
    await apiFetch("/api/auth/logout", { method: "POST" });
  } catch {
    // Server unreachable; continue anyway
  } finally {
    resetSkillsCatalogStore();
    resetUserFilesStore();
  }
}

/**
 * Cooldown after a reauth attempt (success OR failure). When this many
 * milliseconds haven't elapsed since the last attempt, `reauthSilently`
 * short-circuits and returns `null` instead of opening another Office
 * dialog.
 *
 * Why arm the guard on success too: if a 401 fires within 30s of a
 * SUCCESSFUL reauth, the new session itself is being rejected (signing-
 * key mismatch after deploy, server bug, IdP wedge, etc.). Looping the
 * dialog forever in that case is strictly worse than dropping the user
 * onto the login screen with a single visible button.
 *
 * Why arm it on failure too: a freshly cancelled / errored reauth
 * shouldn't be retried by every concurrent in-flight request — that
 * would queue dialog-after-dialog and infuriate the user.
 *
 * 30s is comfortably longer than the server-side 5s refresh-lock TTL
 * but well under any user-perceptible "I just signed in" window, so
 * a real persistent auth issue surfaces quickly.
 */
const REAUTH_LOOP_GUARD_MS = 30_000;

let reauthInFlight: Promise<AuthState | null> | null = null;
let lastReauthAttemptAt = 0;

/**
 * Coordinator for silent FactSet SSO reauth.
 *
 * Used by two consumers, both of which need to share state:
 *
 *  - `fetchWithRetry` (utils/http.ts): on 401, after a no-op
 *    `refreshSession()` fails, awaits this before deciding to give
 *    up. If reauth succeeds, the original request is transparently
 *    retried so the caller never sees the 401 — and never renders
 *    the "Please sign in to continue" error chip.
 *  - `useAuth` (hooks/useAuth.ts): on the `AUTH_EXPIRED_EVENT`,
 *    awaits this so React state ends up consistent with whichever
 *    branch (silent retry success / fall-through to login) the
 *    fetch wrapper ended on.
 *
 * Contract:
 *  - **Single-flight.** Concurrent callers share one in-flight promise
 *    so 401 bursts open exactly one Office dialog (which is required
 *    anyway — `displayDialogAsync` rejects a second concurrent call).
 *  - **Loop guard.** Returns `null` without attempting if another
 *    attempt happened in the last `REAUTH_LOOP_GUARD_MS`, regardless
 *    of that attempt's outcome.
 *  - **Build gate.** Returns `null` immediately on the AuthKit build
 *    (no equivalent of the FactSet "open SSO with this connection"
 *    shortcut).
 *  - Returns the new `AuthState` on success, `null` on any failure
 *    mode (user cancelled, dialog already open, popup blocked, IdP
 *    error, network blip).
 *
 * Never throws — failure is signalled with `null` so callers can
 * branch cleanly without try/catch boilerplate.
 */
export async function reauthSilently(): Promise<AuthState | null> {
  if (!shouldAttemptFactSetAutoLogin()) return null;
  if (reauthInFlight) return reauthInFlight;
  if (Date.now() - lastReauthAttemptAt < REAUTH_LOOP_GUARD_MS) return null;

  reauthInFlight = (async (): Promise<AuthState | null> => {
    try {
      const state = await login({ method: "factset" });
      return state.isAuthenticated ? state : null;
    } catch {
      return null;
    }
  })();

  try {
    return await reauthInFlight;
  } finally {
    lastReauthAttemptAt = Date.now();
    reauthInFlight = null;
  }
}
